'use client';
import React from 'react';

/**
 * GA4, with the measurement ID validated by shape rather than by presence.
 *
 * Both halves are load-bearing and both were learned from live sites in this
 * portfolio. ip-four.co.uk used a bare `if (!ID) return null`, so a deploy that
 * lost the variable shipped no analytics and no error. aerialintel.co.uk had
 * NEXT_PUBLIC_GA_MEASUREMENT_ID set to a placeholder string in a committed
 * .env, so `|| fallback` never fired: the variable was set, just set to
 * nonsense, and it rendered a tag reporting nowhere.
 *
 * netru.io proved the fallback matters. Its Netlify project had no environment
 * variables at all, and analytics kept working only because the ID is hardcoded
 * here rather than read from the environment and hoped for.
 *
 * Plain <script> rather than next/script, because a next/script tag is absent
 * from the served HTML and the installation then cannot be confirmed by
 * viewing source.
 */
const PRODUCTION_MEASUREMENT_ID = 'G-YE18W5R29E';
const VALID_ID = /^G-[A-Z0-9]{6,}$/;

const configured = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim();
const GA_MEASUREMENT_ID =
  configured && VALID_ID.test(configured) ? configured : PRODUCTION_MEASUREMENT_ID;

export default function Analytics() {
  if (!VALID_ID.test(GA_MEASUREMENT_ID)) return null;
  return (
    <>
      <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`} />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}
// Headless-automation guard.
//
// ip-four.co.uk's GA4 property carried 1,043 Direct sessions in the 90 days to
// 2026-09-29 at 12% engagement, 10 seconds and 1.1 pages. 361 came from
// Singapore at 2% engaged and 69 from Ashburn at 0%: AWS and GCP regions, not
// customers. That is 80% of the property's traffic, and it made every
// engagement rate, channel comparison and conversion rate on the account
// meaningless.
//
// navigator.webdriver is set by Chrome, Firefox and Safari whenever the browser
// is driven by WebDriver, which is how most of this traffic arrives. It does not
// catch everything, and it is not a substitute for reading Organic rather than
// Direct, but it costs nothing and removes the bulk of it at source.
if (!navigator.webdriver) {
  gtag('js',new Date());gtag('config','${GA_MEASUREMENT_ID}');
}`,
        }}
      />
    </>
  );
}
